import React, { useState, useEffect } from "react";
import {
    Card,
    Typography,
    Empty,
    Space,
    Tag,
    Button,
    Switch,
    Select,
    Alert,
    message,
    Divider,
    Row,
    Col,
    Spin,
    Tooltip,
    Input,
    Radio
} from 'antd';
import {
    BellOutlined,
    InfoCircleOutlined,
    MobileOutlined,
    LinkOutlined,
    SettingOutlined,
    QrcodeOutlined,
    SlackOutlined
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../hooks/useAuth';
import { Utils } from '../../utils/utils';
import {
    useGetNotificationTopicsQuery,
    useGetNotificationSubscriptionsQuery,
    useCreateNotificationSubscriptionMutation,
    useUpdateNotificationSubscriptionMutation,
    useDeleteNotificationSubscriptionMutation,
    useGetNotificationPreferencesQuery,
    useUpdateNotificationPreferencesMutation,
} from '../../services/nestjs-base-api';
import { EVENT_TYPE_COLORS, formatEventType } from '../../admin/utils/eventTypeUtils';

const { Title, Text } = Typography;
const { Option } = Select;

// IMPORTANT: When adding new event types to the backend (src/events/enums/event-type.enum.ts),
// you MUST also add them to this list to make them available in the notification filters!
// This ensures super admins can subscribe to all event types.
// Also update the list in:
// - src/admin/pages/AdminEventsPage.tsx (EVENT_TYPE_OPTIONS)
// - src/admin/utils/eventTypeUtils.ts (EVENT_TYPE_COLORS)
const ALL_EVENT_TYPES = [
    // User events
    'USER_REGISTERED',
    'USER_LOGIN',
    'USER_LOGOUT',
    'USER_PROFILE_UPDATED',
    'USER_PASSWORD_CHANGED',
    'USER_PASSWORD_SET',
    'USER_IMPERSONATED',
    'USER_IMPERSONATION_STARTED',
    'USER_IMPERSONATION_ENDED',
    'USER_LOGIN_FAILED',
    'USER_ACCOUNT_LOCKED',
    'USER_ACCOUNT_UNLOCKED',

    // Organization events
    'ORGANIZATION_CREATED',
    'ORGANIZATION_UPDATED',
    'ORGANIZATION_STATUS_UPDATED',
    'ORGANIZATION_SETTINGS_CHANGED',
    'ORGANIZATION_FEATURES_UPDATED',
    'ORGANIZATION_DELETED',

    // Brand events
    'BRAND_CREATED',
    'BRAND_UPDATED',
    'BRAND_NAME_UPDATED',
    'BRAND_DELETED',

    // Cohort events
    'COHORT_CREATED',
    'COHORT_UPDATED',
    'COHORT_DEACTIVATED',
    'COHORT_DELETED',

    // Survey events
    'SURVEY_CREATED',
    'SURVEY_UPDATED',
    'SURVEY_PUBLISHED',
    'SURVEY_PAUSED',
    'SURVEY_DELETED',
    'SURVEY_RESPONSE_RECEIVED',
    'SURVEY_RESPONSE_DELETED',
    'SURVEY_LINK_NOT_FOUND',
    'SURVEY_LINK_SALVAGED',

    // Admin events
    'ADMIN_USER_CREATED',
    'ADMIN_USER_UPDATED',
    'ADMIN_USER_DELETED',
    'ADMIN_USER_PASSWORD_RESET',

    // Organization user events
    'ORGANIZATION_USER_CREATED',

    // System events
    'API_KEY_CREATED',
    'API_KEY_REVOKED',
    'PUBLIC_API_PARTICIPANTS_FETCHED',
    'BILLING_EVENT',
    'DATA_EXPORT_REQUESTED',
    'SYSTEM_MAINTENANCE',
    'DATABASE_BACKUP_CREATED',
    'DATABASE_BACKUP_COMPLETED',
    'DATABASE_BACKUP_FAILED',
    'DATABASE_BACKUP_DELETED',
    'DATABASE_BACKUP_RETENTION_CLEANUP',
    'RATE_LIMIT_SETTINGS_UPDATED',
    'RATE_LIMIT_EXCEEDED',

    // Message events
    'MESSAGE_SENT_BY_ADMIN',
    'MESSAGE_RECEIVED',
    'MESSAGE_READ',
    'MESSAGE_MARKED_UNREAD',
    'MESSAGE_STARRED',
    'MESSAGE_UNSTARRED',
    'MESSAGE_ARCHIVED',
    'MESSAGE_UNARCHIVED',
    'MESSAGE_DELETED',
    'MESSAGE_PERMANENTLY_DELETED',
    'MESSAGES_BULK_UPDATED',
    'MESSAGES_ALL_MARKED_READ',
    'MESSAGES_TRASH_EMPTIED',
    'MESSAGE_PREFERENCES_UPDATED',
    'MESSAGE_UNSUBSCRIBED',
    'MESSAGE_TYPE_UNSUBSCRIBED',

    // Response Monitoring events
    'RESPONSE_MONITORING_CHECK_TRIGGERED',
    'RESPONSE_MONITORING_MESSAGE_GENERATED',
    'RESPONSE_MONITORING_MESSAGE_APPROVED',
    'RESPONSE_MONITORING_MESSAGE_REJECTED',
    'RESPONSE_MONITORING_MESSAGE_SENT',
    'RESPONSE_MONITORING_MESSAGE_REGENERATED',
    'RESPONSE_MONITORING_TEST_EMAIL_SENT',
    'RESPONSE_MONITORING_CONFIG_UPDATED',
    'RESPONSE_MONITORING_SCHEDULE_UPDATED',

    // AI Expert events
    'AI_EXPERT_CONVERSATION_CREATED',
    'AI_EXPERT_CONVERSATION_DELETED',
    'AI_EXPERT_MESSAGE_SENT',
    'AI_EXPERT_DOCUMENT_UPLOADED',
    'AI_EXPERT_DOCUMENT_DELETED',
    'AI_EXPERT_CONFIG_UPDATED',
    'AI_EXPERT_TOGGLED',
    'AI_EXPERT_CREATED',

    // Brand Knowledge Base events
    'BRAND_KNOWLEDGE_PROFILE_UPDATED',
    'BRAND_KNOWLEDGE_DOCUMENT_ADDED',
    'BRAND_KNOWLEDGE_DOCUMENT_UPDATED',
    'BRAND_KNOWLEDGE_DOCUMENT_DELETED',
    // Embedded CRM events
    'CRM_COMPANY_CREATED',
    'CRM_COMPANY_UPDATED',
    'CRM_COMPANY_LINKED',
    'CRM_COMPANY_UNLINKED',
    'CRM_STAGE_CHANGED',
    'CRM_NOTE_ADDED',
    'CRM_TASK_CREATED',
    'CRM_TASK_COMPLETED',
    'CRM_SHEET_SYNC_COMPLETED',
    'CRM_SHEET_SYNC_FAILED',
    'CRM_EMAIL_SENT',
    'PLATFORM_EMAIL_SENT',
    'CRM_EMAIL_RECEIVED',
    'CRM_INBOX_SYNCED',
    'CRM_REMINDER_SENT',

    // The Hypothesis events (SYR-90)
    'HYPOTHESIS_RESEARCH_STARTED',
    'HYPOTHESIS_RESEARCH_COMPLETED',
    'HYPOTHESIS_RESEARCH_INSUFFICIENT_DATA',
    'HYPOTHESIS_AWAITING_REVIEWS',
    'HYPOTHESIS_RESEARCH_FAILED',
    'HYPOTHESIS_FORMED',
    'HYPOTHESIS_SUBJECT_DELETED',
    'HYPOTHESIS_TEASE_SENT',
    'HYPOTHESIS_DISMISSED',
    'HYPOTHESIS_CONFIRMED',
    'HYPOTHESIS_OVERTURNED',
    'HYPOTHESIS_REVEAL_SENT',
    // Auto-Concierge (SYR-105)
    'CONCIERGE_PACK_QUEUED',
    'CONCIERGE_PACK_GENERATED',
    'CONCIERGE_PACK_GENERATION_FAILED',
    'CONCIERGE_PACK_EDITED',
    'CONCIERGE_PACK_SENT',
    'CONCIERGE_PACK_KILLED',
    'CONCIERGE_IDENTITY_VERIFICATION_SET',
    'CONCIERGE_OFFER_SET',
    // Charter Cohort (SYR-106)
    'CHARTER_APPLICATION_SUBMITTED',
    'CHARTER_APPLICATION_SPAM_REJECTED',
    'CHARTER_APPLICATION_ACCEPTED',
    'CHARTER_APPLICATION_HELD',
    'CHARTER_APPLICATION_UNSUPPORTED',
    'CHARTER_APPLICATION_UNREVIEWED',
    'CHARTER_ORGANISATION_PROVISIONED',
    'CHARTER_TRIAL_START_BLOCKED',
    // Pricing strategy (SYR-109): a no-free/no-trial signup completed its
    // first checkout — worth subscribing to; it is money arriving.
    'PAID_SIGNUP_COMPLETED',
    // Public Hypothesis lead generator (SYR-107). QUEUED_FOR_REVIEW is the one
    // worth subscribing to: a real brand wanted this and did not clear the
    // gate, which is a conversation rather than a rejection.
    'PUBLIC_HYPOTHESIS_REQUESTED',
    'PUBLIC_HYPOTHESIS_PIN_VERIFIED',
    'PUBLIC_HYPOTHESIS_QUEUED_FOR_REVIEW',
    'PUBLIC_HYPOTHESIS_BLOCKED',
    'PUBLIC_HYPOTHESIS_GENERATED',
    'PUBLIC_HYPOTHESIS_SERVED_CACHED',
    'PUBLIC_HYPOTHESIS_RESULT_SENT',
    'PUBLIC_HYPOTHESIS_INSUFFICIENT_DATA',
    'PUBLIC_HYPOTHESIS_FAILED',
    'PUBLIC_HYPOTHESIS_SPAM_REJECTED',
    'PUBLIC_HYPOTHESIS_UNSUBSCRIBED',
    'PUBLIC_HYPOTHESIS_LEAD_TO_CRM',
    'PUBLIC_HYPOTHESIS_LEAD_DELETED',
    'PUBLIC_HYPOTHESIS_RESULT_LINK_COPIED',
    'HYPOTHESIS_PROVIDER_DEGRADED',
    'PUBLIC_HYPOTHESIS_VIDEO_PLAYED',

    // Email delivery lifecycle events (SES webhook)
    'EMAIL_DELIVERED',
    'EMAIL_OPENED',
    'EMAIL_CLICKED',
    'EMAIL_BOUNCED',
    'EMAIL_COMPLAINT_RECEIVED',

    // Dashboard AI events
    'DASHBOARD_AI_INSIGHT_GENERATED',
    'DASHBOARD_AI_CONFIG_UPDATED',
    'DASHBOARD_AI_CACHE_CLEARED',

    // Advice AI events
    'ADVICE_AI_INSIGHT_GENERATED',
    'ADVICE_AI_CONFIG_UPDATED',
    'ADVICE_AI_CACHE_CLEARED',

    // Orbital AI events
    'ORBITAL_AI_ITEMS_GENERATED',
    'ORBITAL_AI_CONFIG_UPDATED',
    'ORBITAL_AI_CACHE_CLEARED',

    // Building Blocks AI events
    'BUILDING_BLOCKS_AI_INSIGHTS_GENERATED',
    'BUILDING_BLOCKS_AI_CONFIG_UPDATED',
    'BUILDING_BLOCKS_AI_CACHE_CLEARED',

    // AI System Config events
    'AI_SYSTEM_CONFIG_UPDATED',
    'AI_PROVIDER_TEST_SUCCESS',
    'AI_PROVIDER_TEST_FAILED',
    'AI_PROVIDER_FALLBACK_USED',

    // AI Quota events
    'AI_QUOTA_ALERT_SENT',
    'AI_QUOTA_WARNING_SENT',
    'AI_QUOTA_EXCEEDED',
    'AI_QUOTA_UPDATED',
    'AI_QUOTA_RESET',

    // Brand Analytics events
    'BRAND_ANALYTICS_VIEWED',
    'BRAND_ANALYTICS_DRILLDOWN_ACCESSED',

    // Custom Brand Charts events
    'CUSTOM_CHART_CREATED',
    'CUSTOM_CHART_UPDATED',
    'CUSTOM_CHART_DELETED',
    'CUSTOM_CHARTS_REORDERED',

    // Scheduled Job Lock events
    'SCHEDULED_JOB_STARTED',
    'SCHEDULED_JOB_COMPLETED',
    'SCHEDULED_JOB_FAILED',
    'SCHEDULED_JOB_LOCK_ACQUIRED',
    'SCHEDULED_JOB_LOCK_RELEASED',
    'SCHEDULED_JOB_LOCK_FAILED',
    'SCHEDULED_JOB_LOCK_EXPIRED',
    'SCHEDULED_JOB_FORCE_UNLOCKED',

    // CloudWatch Logs events
    'CLOUDWATCH_LOGS_ACCESSED',
    'CLOUDWATCH_LOGS_TAILED',
    'CLOUDWATCH_LOGS_TAIL_ENDED',
    'CLOUDWATCH_LOGS_SEARCHED',

    // Support Ticket events
    'TICKET_CREATED',
    'TICKET_UPDATED',
    'TICKET_COMMENT_ADDED',
    'TICKET_ATTACHMENT_UPLOADED',
    'TICKET_ATTACHMENT_DELETED',
    // Free Trial events
    'FREE_TRIAL_STARTED',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_MET',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_FAILED',
    'FREE_TRIAL_DOWNGRADED',
    'FREE_TRIAL_CONVERTED',
    'FREE_TRIAL_EXPIRED',
    'FREE_TRIAL_NOTIFICATION_SENT',
    'FREE_TRIAL_RESET',
    'FREE_TRIAL_NOTIFICATION_SUPPRESSED',
    'WAITLIST_ENTRY_CREATED',
    'WAITLIST_ENTRY_UPDATED',
    'WAITLIST_STATUS_CHANGED',
    'CORPORATE_SITE_PUBLISHED',
    'CORPORATE_SITE_REVERTED',
    // Response Integrity events (SYR-48)
    'RESPONSE_INTEGRITY_SCORED',
    'RESPONSE_INTEGRITY_FLAGGED',
    'RESPONSE_INTEGRITY_REJECTED_MANUALLY',
    'RESPONSE_INTEGRITY_CONFIRMED_CLEAN',
    'RESPONSE_INTEGRITY_RESCORED',
    'RESPONSE_INTEGRITY_BULK_REJECTED',
    'RESPONSE_INTEGRITY_BULK_CONFIRMED_CLEAN',
    'RESPONSE_INTEGRITY_BULK_RESCORED',
    'RESPONSE_INTEGRITY_DETECTOR_CONFIG_UPDATED',
    'RESPONSE_INTEGRITY_PROFILE_UPDATED',
    'RESPONSE_INTEGRITY_LLM_BUDGET_ALERT',
    'RESPONSE_INTEGRITY_LLM_BUDGET_EXHAUSTED',
    'RESPONSE_INTEGRITY_EVIDENCE_ARCHIVED',
    // Panel Chargeback events (SYR-48)
    'PANEL_CHARGEBACK_PREPARED',
    'PANEL_CHARGEBACK_SUBMITTED',
    'PANEL_CHARGEBACK_RESOLVED',
];

const Notifications: React.FunctionComponent = () => {
    const { user } = useAuth();
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
    const [selectedPriority, setSelectedPriority] = useState<number>(3);
    const [notificationType, setNotificationType] = useState<'ntfy' | 'slack'>('ntfy');
    const [slackWebhookUrl, setSlackWebhookUrl] = useState<string>('');
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    
    // Check if user is admin or super admin
    const isAdmin = user && (Utils.userIsAdmin(user) || Utils.userIsSuperAdmin(user));
    
    // API queries and mutations
    const { data: topics = [], isLoading: topicsLoading } = useGetNotificationTopicsQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !isAdmin || !user?.jwtToken }
    );
    
    const { data: subscriptions = [], isLoading: subscriptionsLoading, refetch } = useGetNotificationSubscriptionsQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !isAdmin || !user?.jwtToken }
    );
    
    const [createSubscription, { isLoading: isCreating }] = useCreateNotificationSubscriptionMutation();
    const [updateSubscription, { isLoading: isUpdating }] = useUpdateNotificationSubscriptionMutation();
    const [deleteSubscription, { isLoading: isDeleting }] = useDeleteNotificationSubscriptionMutation();
    
    // Notification preferences queries
    const { data: notificationPrefs, isLoading: prefsLoading } = useGetNotificationPreferencesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !isAdmin || !user?.jwtToken }
    );
    const [updateNotificationPreferences] = useUpdateNotificationPreferencesMutation();
    
    // Current subscription state
    const allEventsSubscription = subscriptions.find(sub => sub.topicType === 'ALL_EVENTS');
    const isSubscribed = !!allEventsSubscription;
    
    // Initialize state from existing subscription
    useEffect(() => {
        if (allEventsSubscription?.notificationSettings) {
            const settings = allEventsSubscription.notificationSettings;
            setSelectedEventTypes(settings.eventTypes || []);
            setSelectedPriority(settings.priority || 3);
        }
    }, [allEventsSubscription]);
    
    // Initialize notification preferences
    useEffect(() => {
        if (notificationPrefs) {
            setNotificationType(notificationPrefs.notificationType || 'ntfy');
            setSlackWebhookUrl(notificationPrefs.slackWebhookUrl || '');
        }
    }, [notificationPrefs]);
    
    const handleSubscriptionToggle = async (checked: boolean) => {
        try {
            if (checked) {
                // Subscribe
                await createSubscription({
                    jwtToken: user!.jwtToken,
                    payload: {
                        topicType: 'ALL_EVENTS',
                        notificationSettings: {
                            priority: selectedPriority,
                            eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : undefined,
                        }
                    }
                }).unwrap();
                message.success('Successfully subscribed to push notifications!');
            } else {
                // Unsubscribe
                await deleteSubscription({
                    jwtToken: user!.jwtToken,
                    topicType: 'ALL_EVENTS'
                }).unwrap();
                message.success('Successfully unsubscribed from push notifications');
            }
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to update subscription');
        }
    };
    
    const handleEventTypesChange = async (eventTypes: string[]) => {
        setSelectedEventTypes(eventTypes);
        if (isSubscribed) {
            try {
                await updateSubscription({
                    jwtToken: user!.jwtToken,
                    topicType: 'ALL_EVENTS',
                    payload: {
                        notificationSettings: {
                            ...allEventsSubscription.notificationSettings,
                            eventTypes: eventTypes.length > 0 ? eventTypes : undefined,
                        }
                    }
                }).unwrap();
                message.success('Event filters updated');
                refetch();
            } catch (error: any) {
                message.error('Failed to update event filters');
            }
        }
    };
    
    const handlePriorityChange = async (priority: number) => {
        setSelectedPriority(priority);
        if (isSubscribed) {
            try {
                await updateSubscription({
                    jwtToken: user!.jwtToken,
                    topicType: 'ALL_EVENTS',
                    payload: {
                        notificationSettings: {
                            ...allEventsSubscription.notificationSettings,
                            priority,
                        }
                    }
                }).unwrap();
                message.success('Priority updated');
                refetch();
            } catch (error: any) {
                message.error('Failed to update priority');
            }
        }
    };
    
    if (!isAdmin) {
        return (
            <div style={{ padding: '0 0 24px 0' }}>
                <Title level={4} style={{ marginBottom: 24 }}>Notifications</Title>
                <Alert
                    message="Access Restricted"
                    description="Push notifications are only available for platform administrators."
                    type="info"
                    showIcon
                />
            </div>
        );
    }
    
    const handleSaveNotificationPreferences = async () => {
        setIsSavingPreferences(true);
        try {
            await updateNotificationPreferences({
                jwtToken: user!.jwtToken,
                payload: {
                    notificationType,
                    slackWebhookUrl: notificationType === 'slack' ? slackWebhookUrl : undefined,
                }
            }).unwrap();
            message.success('Notification preferences saved successfully');
        } catch (error: any) {
            message.error('Failed to save notification preferences');
        } finally {
            setIsSavingPreferences(false);
        }
    };
    
    if (topicsLoading || subscriptionsLoading || prefsLoading) {
        return (
            <div style={{ padding: '0 0 24px 0', textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>Loading notification settings...</Text>
                </div>
            </div>
        );
    }
    
    const topic = topics[0]; // Assuming we have one topic for now
    
    return (
        <div style={{ padding: '0 0 24px 0' }}>
            <Title level={4} style={{ marginBottom: 24 }}>Push Notifications</Title>
            
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                    <Card title={<Space><BellOutlined />Notification Settings</Space>}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {/* Notification Type Selection */}
                            <div>
                                <Text strong style={{ marginBottom: 8, display: 'block' }}>Notification Channel</Text>
                                <Radio.Group 
                                    value={notificationType} 
                                    onChange={(e) => setNotificationType(e.target.value)}
                                    style={{ marginBottom: 16 }}
                                >
                                    <Space direction="vertical" size="middle">
                                        <Radio value="ntfy">
                                            <Space>
                                                <MobileOutlined />
                                                <div>
                                                    <Text strong>ntfy Push Notifications</Text>
                                                    <br />
                                                    <Text type="secondary">Receive notifications on your mobile device via ntfy app</Text>
                                                </div>
                                            </Space>
                                        </Radio>
                                        <Radio value="slack">
                                            <Space>
                                                <SlackOutlined />
                                                <div>
                                                    <Text strong>Slack Notifications</Text>
                                                    <br />
                                                    <Text type="secondary">Receive notifications in your Slack workspace</Text>
                                                </div>
                                            </Space>
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                                
                                {/* Slack Webhook URL Input */}
                                {notificationType === 'slack' && (
                                    <div style={{ marginTop: 16, marginLeft: 28 }}>
                                        <Text strong style={{ marginBottom: 8, display: 'block' }}>Slack Webhook URL</Text>
                                        <Input
                                            placeholder="https://hooks.slack.com/services/..."
                                            value={slackWebhookUrl}
                                            onChange={(e) => setSlackWebhookUrl(e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        />
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            Create an Incoming Webhook in your Slack workspace and paste the URL here
                                        </Text>
                                    </div>
                                )}
                                
                                <Button 
                                    type="primary" 
                                    onClick={handleSaveNotificationPreferences}
                                    loading={isSavingPreferences}
                                    style={{ marginTop: 8 }}
                                >
                                    Save Notification Preferences
                                </Button>
                            </div>
                            
                            <Divider />
                            
                            {/* Main Toggle */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Text strong>Enable notifications</Text>
                                    <br />
                                    <Text type="secondary">Receive real-time platform activity updates</Text>
                                </div>
                                <Switch 
                                    checked={isSubscribed && allEventsSubscription?.isActive}
                                    onChange={handleSubscriptionToggle}
                                    loading={isCreating || isDeleting}
                                />
                            </div>
                            
                            {isSubscribed && (
                                <>
                                    <Divider />
                                    
                                    {/* Topic Information */}
                                    <div>
                                        <Text strong style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <SettingOutlined />
                                            Topic Details
                                        </Text>
                                        <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
                                            <Text><strong>Topic Name:</strong> {topic?.topicName}</Text>
                                            <br />
                                            <Text type="secondary">Use this topic name in the ntfy mobile app</Text>
                                        </div>
                                    </div>
                                    
                                    {/* Priority Setting */}
                                    <div>
                                        <Text strong style={{ marginBottom: 8, display: 'block' }}>Notification Priority</Text>
                                        <Select
                                            value={selectedPriority}
                                            onChange={handlePriorityChange}
                                            style={{ width: 200 }}
                                            loading={isUpdating}
                                        >
                                            <Option value={1}>🔕 Low (Silent)</Option>
                                            <Option value={2}>🔔 Medium</Option>
                                            <Option value={3}>🔔 High</Option>
                                            <Option value={4}>🚨 Urgent</Option>
                                            <Option value={5}>🚨 Max Priority</Option>
                                        </Select>
                                    </div>
                                    
                                    {/* Event Type Filters */}
                                    <div>
                                        <Text strong style={{ marginBottom: 8, display: 'block' }}>Event Types</Text>
                                        <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                                            Choose which events trigger notifications (leave empty for all events)
                                        </Text>
                                        <Select
                                            mode="multiple"
                                            placeholder="Select event types or leave empty for all events"
                                            value={selectedEventTypes}
                                            onChange={handleEventTypesChange}
                                            style={{ width: '100%' }}
                                            maxTagCount="responsive"
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={ALL_EVENT_TYPES.map(eventType => ({
                                                value: eventType,
                                                label: formatEventType(eventType),
                                            }))}
                                            tagRender={(props) => {
                                                const { label, value, closable, onClose } = props;
                                                return (
                                                    <Tag
                                                        color={EVENT_TYPE_COLORS[value as string] || 'default'}
                                                        closable={closable}
                                                        onClose={onClose}
                                                        style={{ marginRight: 3 }}
                                                    >
                                                        {label}
                                                    </Tag>
                                                );
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </Space>
                    </Card>
                </Col>
                
                <Col xs={24} lg={8}>
                    <Card title={<Space>{notificationType === 'slack' ? <SlackOutlined /> : <MobileOutlined />}{notificationType === 'slack' ? 'Slack' : 'Mobile'} Setup</Space>}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {isSubscribed ? (
                                <>
                                    {notificationType === 'slack' ? (
                                        <>
                                            <Alert
                                                message="Slack Integration"
                                                description={slackWebhookUrl ? "Your Slack webhook is configured. You'll receive notifications in your Slack workspace." : "Please configure your Slack webhook URL to receive notifications."}
                                                type={slackWebhookUrl ? "success" : "warning"}
                                                showIcon
                                            />
                                            
                                            <div>
                                                <Text strong style={{ marginBottom: 8, display: 'block' }}>How to set up Slack notifications:</Text>
                                                <ol style={{ paddingLeft: 20, margin: 0 }}>
                                                    <li style={{ marginBottom: 8 }}>
                                                        <Text>Go to your Slack workspace</Text>
                                                    </li>
                                                    <li style={{ marginBottom: 8 }}>
                                                        <Text>Navigate to <strong>Apps → Incoming Webhooks</strong></Text>
                                                    </li>
                                                    <li style={{ marginBottom: 8 }}>
                                                        <Text>Create a new webhook for your desired channel</Text>
                                                    </li>
                                                    <li style={{ marginBottom: 8 }}>
                                                        <Text>Copy the webhook URL and paste it above</Text>
                                                    </li>
                                                </ol>
                                            </div>
                                            
                                            <Button 
                                                type="primary" 
                                                icon={<LinkOutlined />}
                                                onClick={() => window.open('https://api.slack.com/messaging/webhooks', '_blank')}
                                                style={{ width: '100%' }}
                                            >
                                                Slack Webhook Documentation
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Alert
                                                message="Setup Complete!"
                                                description="You're subscribed to notifications. Complete the mobile setup below."
                                                type="success"
                                                showIcon
                                            />
                                    
                                    {/* QR Code Section */}
                                    <div style={{ textAlign: 'center' }}>
                                        <Text strong style={{ marginBottom: 12, display: 'block' }}>
                                            <QrcodeOutlined style={{ marginRight: 8 }} />
                                            Quick Setup
                                        </Text>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            marginBottom: 16,
                                            padding: 16,
                                            backgroundColor: '#ffffff',
                                            borderRadius: 8,
                                            border: '1px solid #d9d9d9'
                                        }}>
                                            <QRCodeSVG
                                                value={topic?.subscriptionUrl || ''}
                                                size={120}
                                                bgColor="#ffffff"
                                                fgColor="#000000"
                                                level="M"
                                                includeMargin={true}
                                            />
                                        </div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 16 }}>
                                            Scan with your phone camera to open the topic directly
                                        </Text>
                                    </div>
                                    
                                    <Divider>or follow these steps</Divider>
                                    
                                    <div>
                                        <Text strong style={{ marginBottom: 8, display: 'block' }}>📱 Manual Setup</Text>
                                        <ol style={{ paddingLeft: 20, margin: 0 }}>
                                            <li style={{ marginBottom: 8 }}>
                                                <Text>Download the <strong>ntfy</strong> app from your app store</Text>
                                            </li>
                                            <li style={{ marginBottom: 8 }}>
                                                <Text>Subscribe to topic: </Text>
                                                <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                                                    {topic?.topicName}
                                                </Tag>
                                            </li>
                                            <li style={{ marginBottom: 8 }}>
                                                <Text>Enable notifications in your phone settings</Text>
                                            </li>
                                        </ol>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Button 
                                            type="primary" 
                                            icon={<LinkOutlined />}
                                            onClick={() => window.open(topic?.subscriptionUrl, '_blank')}
                                            style={{ flex: 1 }}
                                        >
                                            Open Link
                                        </Button>
                                        <Tooltip title="Copy topic URL">
                                            <Button 
                                                icon={<QrcodeOutlined />}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(topic?.subscriptionUrl || '');
                                                    message.success('Topic URL copied to clipboard!');
                                                }}
                                            />
                                        </Tooltip>
                                    </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <Alert
                                    message="Not Subscribed"
                                    description="Enable push notifications above to get started with mobile setup."
                                    type="info"
                                    showIcon
                                />
                            )}
                        </Space>
                    </Card>
                    
                    {isSubscribed && (
                        <Card title="Recent Activity" style={{ marginTop: 16 }}>
                            <Empty
                                image={<InfoCircleOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />}
                                description={
                                    <span>
                                        Notification history
                                        <br />
                                        <Text type="secondary">Coming soon</Text>
                                    </span>
                                }
                                imageStyle={{ height: 60 }}
                            />
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default Notifications;